"use client";

import { useState } from "react";
import Header from "@/components/common/Header";
import Footer from "@/components/common/Footer";
import Icon from "@/components/ui/AppIcon";
import Link from "next/link";

interface WizardState {
  process: string;
  erp: string;
  style: string;
  painPoint: string;
  theme: string;
}

interface RecommendationResult {
  engagement: string;
  engagementIcon: string;
  engagementColor: string;
  description: string;
  nextSteps: string;
  ctaLink: string;
  ctaText: string;
}

export default function WizardPage() {
  const [step, setStep] = useState(1);
  const [wizardData, setWizardData] = useState<WizardState>({
    process: "",
    erp: "",
    style: "",
    painPoint: "",
    theme: "",
  });
  const [recommendation, setRecommendation] = useState<RecommendationResult | null>(null);

  const processes = [
    { id: "otc", label: "Order-to-Cash (OTC)", icon: "ShoppingCartIcon" },
    { id: "p2p", label: "Procure-to-Pay (P2P)", icon: "CreditCardIcon" },
    { id: "rtr", label: "Record-to-Report (RTR)", icon: "DocumentTextIcon" },
    { id: "logistics", label: "Inventory & Logistics", icon: "TruckIcon" },
    { id: "manufacturing", label: "Manufacturing", icon: "CogIcon" },
    { id: "subscription", label: "Subscription & Usage", icon: "ArrowPathIcon" },
    { id: "intercompany", label: "Intercompany", icon: "BuildingOfficeIcon" },
    { id: "tax", label: "Tax & Compliance", icon: "DocumentCheckIcon" },
  ];

  const erpSystems = [
    { id: "sap", label: "SAP" },
    { id: "oracle", label: "Oracle" },
    { id: "netsuite", label: "NetSuite" },
    { id: "workday", label: "Workday" },
    { id: "dynamics", label: "Microsoft Dynamics" },
    { id: "other", label: "Other/Multiple" },
  ];

  const styles = [
    { id: "single", label: "Single Interface", description: "One specific integration, quick win" },
    { id: "process", label: "End-to-End Process", description: "Complete process flow (e.g., full OTC)" },
    { id: "program", label: "Ongoing Program", description: "Embedded team, multiple workstreams" },
  ];

  const painPoints = [
    { id: "new", label: "New Implementation", description: "Starting fresh, need design & build" },
    { id: "broken", label: "Broken/Failing", description: "Production issues, need rescue" },
    { id: "advisory", label: "Need Guidance", description: "Architecture review, best practices" },
    { id: "scaling", label: "Scaling Up", description: "Growing complexity, need governance" },
  ];

  const themes = [
    {
      id: "startup-modern",
      label: "Startup Modern",
      modernScore: 5,
      maturityScore: 1,
      description: "Bold gradients, vibrant colors, playful animations",
      preview: "from-purple-500 via-pink-500 to-orange-500",
      textColor: "text-white",
    },
    {
      id: "tech-forward",
      label: "Tech Forward",
      modernScore: 4,
      maturityScore: 2,
      description: "Clean lines, neon accents, glassmorphic effects",
      preview: "from-cyan-500 via-blue-500 to-indigo-600",
      textColor: "text-white",
    },
    {
      id: "balanced-professional",
      label: "Balanced Professional",
      modernScore: 3,
      maturityScore: 3,
      description: "Refined aesthetics, subtle gradients, professional polish",
      preview: "from-blue-600 via-indigo-600 to-slate-700",
      textColor: "text-white",
    },
    {
      id: "corporate-refined",
      label: "Corporate Refined",
      modernScore: 2,
      maturityScore: 4,
      description: "Muted tones, structured layouts, conservative elegance",
      preview: "from-slate-600 via-gray-700 to-neutral-800",
      textColor: "text-neutral-200",
    },
    {
      id: "enterprise-traditional",
      label: "Enterprise Traditional",
      modernScore: 1,
      maturityScore: 5,
      description: "Classic navy, minimal effects, timeless design",
      preview: "from-slate-800 via-slate-900 to-neutral-900",
      textColor: "text-neutral-300",
    },
  ];

  const handleSelect = (field: keyof WizardState, value: string) => {
    const newData = { ...wizardData, [field]: value };
    setWizardData(newData);

    if (step < 5) {
      setStep(step + 1);
    } else {
      // Generate recommendation
      const result = generateRecommendation(newData);
      setRecommendation(result);
      setStep(6);
    }
  };

  const generateRecommendation = (data: WizardState): RecommendationResult => {
    // Rescue/Stabilization logic
    if (data.painPoint === "broken") {
      return {
        engagement: "Rescue/Stabilization",
        engagementIcon: "ExclamationTriangleIcon",
        engagementColor: "text-red-400",
        description: `We'll triage your ${getProcessLabel(data.process)} integrations in ${data.erp.toUpperCase()}, identify root causes, and stabilize production within 2-6 weeks.`,
        nextSteps: "Root cause analysis → Hotfixes → Stabilization plan → Knowledge transfer",
        ctaLink: "/contact?topic=stabilization",
        ctaText: "Request Rescue Engagement",
      };
    }

    // Architect-on-Demand logic
    if (data.painPoint === "advisory") {
      return {
        engagement: "Architect-on-Demand",
        engagementIcon: "UserCircleIcon",
        engagementColor: "text-green-400",
        description: `Part-time advisory (10-20 hours/month) for your ${getProcessLabel(data.process)} architecture in ${data.erp.toUpperCase()}. Design reviews, best practices, technical guidance.`,
        nextSteps: "Monthly retainer → 2-4 review sessions → Async support via Slack/email",
        ctaLink: "/contact?topic=architect",
        ctaText: "Request Advisory Engagement",
      };
    }

    // Program Office logic
    if (data.style === "program" || data.painPoint === "scaling") {
      return {
        engagement: "Program Integration Office",
        engagementIcon: "UsersIcon",
        engagementColor: "text-blue-400",
        description: `Embedded team managing your ${getProcessLabel(data.process)} integrations across ${data.erp.toUpperCase()}. Governance, backlog management, sprint-based delivery.`,
        nextSteps: "Setup & Governance → Sprint-based delivery (2-week sprints) → Quarterly milestones",
        ctaLink: "/contact?topic=delivery",
        ctaText: "Request Program Office",
      };
    }

    // Process-wide logic
    if (data.style === "process") {
      return {
        engagement: "Process-wide Integration",
        engagementIcon: "ArrowsRightLeftIcon",
        engagementColor: "text-blue-400",
        description: `End-to-end ${getProcessLabel(data.process)} integration for ${data.erp.toUpperCase()}. Complete process blueprint, all interfaces, testing framework, observability.`,
        nextSteps: "Design & Prototyping (4 weeks) → Build & Testing (8 weeks) → UAT & Go-live (4 weeks)",
        ctaLink: "/contact?topic=delivery",
        ctaText: "Request Process Integration",
      };
    }

    // Default: Single Interface
    return {
      engagement: "Single Interface Delivery",
      engagementIcon: "LinkIcon",
      engagementColor: "text-blue-400",
      description: `One ${getProcessLabel(data.process)} interface for ${data.erp.toUpperCase()}. Fixed scope, 2-8 weeks. Design spec, code, testing, deployment guide, runbook.`,
      nextSteps: "Design (1-2 weeks) → Build & Test (3-6 weeks) → Deploy & Handoff (1-2 weeks)",
      ctaLink: "/contact?topic=delivery",
      ctaText: "Request Single Interface",
    };
  };

  const getProcessLabel = (processId: string): string => {
    const process = processes.find((p) => p.id === processId);
    return process ? process.label : processId;
  };

  const handleReset = () => {
    setStep(1);
    setWizardData({ process: "", erp: "", style: "", painPoint: "", theme: "" });
    setRecommendation(null);
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  return (
    <>
      <Header />
      <main className="min-h-screen bg-background text-foreground pt-20">
        {/* Page Header */}
        <section className="py-24 px-6 border-b border-white/5">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Find Your Integration Fit
            </h1>
            <p className="text-xl text-neutral-400">
              Answer 4 questions to discover the right engagement model for your needs.
            </p>
          </div>
        </section>

        {/* Wizard Flow */}
        <section className="py-24 px-6">
          <div className="max-w-4xl mx-auto">
            {/* Progress Bar */}
            <div className="mb-12">
              <div className="flex items-center justify-between mb-4">
                {[1, 2, 3, 4, 5].map((s) => (
                  <div
                    key={s}
                    className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-colors ${
                      step >= s
                        ? "bg-blue-500 border-blue-500 text-white" :"bg-neutral-800 border-white/10 text-neutral-500"
                    }`}
                  >
                    {s}
                  </div>
                ))}
              </div>
              <div className="h-2 bg-neutral-800 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 transition-all duration-500"
                  style={{ width: `${(Math.min(step, 5) / 5) * 100}%` }}
                />
              </div>
            </div>

            {/* Step 1: Process */}
            {step === 1 && (
              <div className="glass-card rounded-2xl p-8">
                <h2 className="text-3xl font-bold text-white mb-4">
                  Which process are you integrating?
                </h2>
                <p className="text-neutral-400 mb-8">
                  Select the business process that needs integration work.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {processes.map((process) => (
                    <button
                      key={process.id}
                      onClick={() => handleSelect("process", process.id)}
                      className="glass-card glass-card-hover rounded-xl p-6 text-left hover:scale-105 transition-transform"
                    >
                      <Icon
                        name={process.icon as any}
                        size={32}
                        className="text-blue-400 mb-3"
                      />
                      <h3 className="text-lg font-semibold text-white">
                        {process.label}
                      </h3>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Step 2: ERP */}
            {step === 2 && (
              <div className="glass-card rounded-2xl p-8">
                <h2 className="text-3xl font-bold text-white mb-4">
                  What's your ERP system?
                </h2>
                <p className="text-neutral-400 mb-8">
                  Select your primary ERP or enterprise platform.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {erpSystems.map((erp) => (
                    <button
                      key={erp.id}
                      onClick={() => handleSelect("erp", erp.id)}
                      className="glass-card glass-card-hover rounded-xl p-6 text-left hover:scale-105 transition-transform"
                    >
                      <h3 className="text-lg font-semibold text-white">
                        {erp.label}
                      </h3>
                    </button>
                  ))}
                </div>
                <button
                  onClick={handleBack}
                  className="mt-6 text-neutral-400 hover:text-white transition-colors flex items-center gap-2"
                >
                  <Icon name="ArrowLeftIcon" size={20} />
                  Back
                </button>
              </div>
            )}

            {/* Step 3: Style */}
            {step === 3 && (
              <div className="glass-card rounded-2xl p-8">
                <h2 className="text-3xl font-bold text-white mb-4">
                  What's your preferred engagement style?
                </h2>
                <p className="text-neutral-400 mb-8">
                  Choose the scope that fits your project.
                </p>
                <div className="space-y-4">
                  {styles.map((style) => (
                    <button
                      key={style.id}
                      onClick={() => handleSelect("style", style.id)}
                      className="glass-card glass-card-hover rounded-xl p-6 text-left hover:scale-105 transition-transform w-full"
                    >
                      <h3 className="text-lg font-semibold text-white mb-2">
                        {style.label}
                      </h3>
                      <p className="text-sm text-neutral-400">{style.description}</p>
                    </button>
                  ))}
                </div>
                <button
                  onClick={handleBack}
                  className="mt-6 text-neutral-400 hover:text-white transition-colors flex items-center gap-2"
                >
                  <Icon name="ArrowLeftIcon" size={20} />
                  Back
                </button>
              </div>
            )}

            {/* Step 4: Pain Point */}
            {step === 4 && (
              <div className="glass-card rounded-2xl p-8">
                <h2 className="text-3xl font-bold text-white mb-4">
                  What's your primary pain point?
                </h2>
                <p className="text-neutral-400 mb-8">
                  Help us understand your current situation.
                </p>
                <div className="space-y-4">
                  {painPoints.map((pain) => (
                    <button
                      key={pain.id}
                      onClick={() => handleSelect("painPoint", pain.id)}
                      className="glass-card glass-card-hover rounded-xl p-6 text-left hover:scale-105 transition-transform w-full"
                    >
                      <h3 className="text-lg font-semibold text-white mb-2">
                        {pain.label}
                      </h3>
                      <p className="text-sm text-neutral-400">{pain.description}</p>
                    </button>
                  ))}
                </div>
                <button
                  onClick={handleBack}
                  className="mt-6 text-neutral-400 hover:text-white transition-colors flex items-center gap-2"
                >
                  <Icon name="ArrowLeftIcon" size={20} />
                  Back
                </button>
              </div>
            )}

            {/* Step 5: Theme Picker */}
            {step === 5 && (
              <div className="glass-card rounded-2xl p-8">
                <h2 className="text-3xl font-bold text-white mb-4">
                  Choose your visual theme
                </h2>
                <p className="text-neutral-400 mb-8">
                  Select a style that matches your brand personality.
                </p>

                {/* Scale Indicators */}
                <div className="mb-8 space-y-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-neutral-500">Modern</span>
                    <div className="flex-1 mx-4 h-1 bg-gradient-to-r from-purple-500 via-blue-500 to-slate-800 rounded-full" />
                    <span className="text-neutral-500">Traditional</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-neutral-500">Startup</span>
                    <div className="flex-1 mx-4 h-1 bg-gradient-to-r from-orange-500 via-indigo-500 to-slate-800 rounded-full" />
                    <span className="text-neutral-500">Mature</span>
                  </div>
                </div>

                {/* Theme Options */}
                <div className="space-y-4">
                  {themes.map((theme) => (
                    <button
                      key={theme.id}
                      onClick={() => handleSelect("theme", theme.id)}
                      className="glass-card glass-card-hover rounded-xl p-6 text-left hover:scale-105 transition-transform w-full group"
                    >
                      <div className="flex items-center gap-6">
                        {/* Theme Preview */}
                        <div
                          className={`w-24 h-24 rounded-lg bg-gradient-to-br ${theme.preview} flex items-center justify-center shadow-lg group-hover:shadow-xl transition-shadow`}
                        >
                          <div className="w-16 h-16 bg-white/10 backdrop-blur-sm rounded-lg border border-white/20" />
                        </div>

                        {/* Theme Info */}
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-white mb-2">
                            {theme.label}
                          </h3>
                          <p className="text-sm text-neutral-400 mb-3">
                            {theme.description}
                          </p>

                          {/* Scale Indicators */}
                          <div className="flex gap-6 text-xs">
                            <div className="flex items-center gap-2">
                              <span className="text-neutral-500">Modern</span>
                              <div className="flex gap-1">
                                {[1, 2, 3, 4, 5].map((i) => (
                                  <div
                                    key={i}
                                    className={`w-2 h-2 rounded-full ${
                                      i <= theme.modernScore
                                        ? "bg-purple-500" :"bg-neutral-700"
                                    }`}
                                  />
                                ))}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-neutral-500">Maturity</span>
                              <div className="flex gap-1">
                                {[1, 2, 3, 4, 5].map((i) => (
                                  <div
                                    key={i}
                                    className={`w-2 h-2 rounded-full ${
                                      i <= theme.maturityScore
                                        ? "bg-blue-500" :"bg-neutral-700"
                                    }`}
                                  />
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>

                <button
                  onClick={handleBack}
                  className="mt-6 text-neutral-400 hover:text-white transition-colors flex items-center gap-2"
                >
                  <Icon name="ArrowLeftIcon" size={20} />
                  Back
                </button>
              </div>
            )}

            {/* Step 6: Recommendation */}
            {step === 6 && recommendation && (
              <div className="glass-card rounded-2xl p-8">
                <div className="text-center mb-8">
                  <Icon
                    name={recommendation.engagementIcon as any}
                    size={64}
                    className={`${recommendation.engagementColor} mx-auto mb-4`}
                  />
                  <h2 className="text-4xl font-bold text-white mb-4">
                    {recommendation.engagement}
                  </h2>
                  <p className="text-xl text-neutral-300 mb-6">
                    {recommendation.description}
                  </p>
                </div>

                <div className="bg-neutral-800/50 rounded-xl p-6 mb-8">
                  <h3 className="text-sm font-semibold text-neutral-400 uppercase tracking-wide mb-3">
                    Typical Flow
                  </h3>
                  <p className="text-neutral-300">{recommendation.nextSteps}</p>
                </div>

                <div className="bg-neutral-800/50 rounded-xl p-6 mb-8">
                  <h3 className="text-sm font-semibold text-neutral-400 uppercase tracking-wide mb-3">
                    Your Selections
                  </h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-neutral-500">Process:</span>
                      <span className="text-white ml-2">
                        {getProcessLabel(wizardData.process)}
                      </span>
                    </div>
                    <div>
                      <span className="text-neutral-500">ERP:</span>
                      <span className="text-white ml-2">
                        {wizardData.erp.toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <span className="text-neutral-500">Style:</span>
                      <span className="text-white ml-2">
                        {styles.find((s) => s.id === wizardData.style)?.label}
                      </span>
                    </div>
                    <div>
                      <span className="text-neutral-500">Pain Point:</span>
                      <span className="text-white ml-2">
                        {painPoints.find((p) => p.id === wizardData.painPoint)?.label}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                  <Link
                    href={recommendation.ctaLink}
                    className="flex-1 px-8 py-4 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-full text-center font-medium hover:scale-105 transition-transform"
                  >
                    {recommendation.ctaText}
                  </Link>
                  <button
                    onClick={handleReset}
                    className="flex-1 px-8 py-4 bg-white/5 border border-white/10 text-white rounded-full font-medium hover:bg-white/10 transition-colors"
                  >
                    Start Over
                  </button>
                </div>

                <div className="mt-8 text-center">
                  <Link
                    href="/services"
                    className="text-blue-400 hover:underline text-sm"
                  >
                    View all engagement models →
                  </Link>
                </div>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}